import type { ReactNode } from 'react';
import { BookOpen, Camera, Gift, GraduationCap, Info, RefreshCw, Send, Share2, ShoppingBag, Timer, Users } from 'lucide-react';
import type { CaptureLoopStep } from '@/hooks/useProductEngagementAnalytics';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatNumber } from '@/lib/utils';
import { formatDurationSec } from '@/hooks/useProductSessionAnalytics';
import {
    ACADEMY_SECTION_LABELS,
    EXPERIENCE_LABELS,
    MAIN_TAB_LABELS,
    STORE_SECTION_LABELS,
    WATER_LABELS,
    labelForScreen,
    useProductEngagementAnalytics,
    type AcademyContentStat,
    type EngagementRow,
    type StoreProfileRow,
} from '@/hooks/useProductEngagementAnalytics';

const tooltipStyle = {
    background: 'hsl(224 39% 15%)',
    border: '1px solid hsl(222 30% 22%)',
    borderRadius: 8,
    fontSize: 12,
};

function fmtPct(v: number | null): string {
    if (v == null) return 'n/d';
    return `${v.toLocaleString('es-ES', { maximumFractionDigits: 1 })}%`;
}

function LoopCard({
    icon,
    title,
    step,
    extra,
}: {
    icon: ReactNode;
    title: string;
    step: CaptureLoopStep;
    extra?: string;
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 min-w-0">
            <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-muted-foreground">{title}</p>
                <span className="text-primary shrink-0">{icon}</span>
            </div>
            <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                {formatNumber(step.users)}
                <span className="text-sm font-semibold text-muted-foreground ml-1">
                    ({fmtPct(step.users_pct)})
                </span>
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
                {formatNumber(step.events)} veces
            </p>
            {extra ? <p className="text-[11px] text-muted-foreground mt-1">{extra}</p> : null}
        </div>
    );
}

function ContentCard({
    title,
    stat,
    hint,
}: {
    title: string;
    stat: AcademyContentStat;
    hint: string;
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 min-w-0">
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                {formatNumber(stat.users)}
                <span className="text-sm font-semibold text-muted-foreground ml-1">
                    ({fmtPct(stat.users_pct)})
                </span>
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
                {formatNumber(stat.items)} {hint} · {fmtPct(stat.of_academy_pct)} de quien entra a Academia
            </p>
        </div>
    );
}

function ProfileTable({
    title,
    rows,
    labels,
}: {
    title: string;
    rows: StoreProfileRow[];
    labels: Record<string, string>;
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 min-w-0">
            <p className="text-sm font-semibold text-foreground mb-3">{title}</p>
            {rows.length === 0 ? (
                <p className="text-xs text-muted-foreground">Sin datos de perfil</p>
            ) : (
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[11px] text-muted-foreground text-left">
                            <th className="pb-2 font-medium">Perfil</th>
                            <th className="pb-2 font-medium text-right">Activos</th>
                            <th className="pb-2 font-medium text-right">Entran</th>
                            <th className="pb-2 font-medium text-right">%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.key} className="border-t border-border/60">
                                <td className="py-1.5 text-foreground">{labels[row.key] ?? row.key}</td>
                                <td className="py-1.5 text-right tabular-nums">{formatNumber(row.active_users)}</td>
                                <td className="py-1.5 text-right tabular-nums">{formatNumber(row.visitors)}</td>
                                <td className="py-1.5 text-right tabular-nums font-semibold">{fmtPct(row.visitors_pct)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function ScreenTable({ rows }: { rows: EngagementRow[] }) {
    if (rows.length === 0) {
        return <p className="text-xs text-muted-foreground">Aún no hay page_view en este periodo</p>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-[11px] text-muted-foreground text-left">
                        <th className="pb-2 font-medium">Apartado</th>
                        <th className="pb-2 font-medium text-right">Visitas</th>
                        <th className="pb-2 font-medium text-right">Usuarios</th>
                        <th className="pb-2 font-medium text-right">% activos</th>
                        <th className="pb-2 font-medium text-right">Tiempo p50</th>
                        <th className="pb-2 font-medium text-right">Tiempo medio</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key} className="border-t border-border/60">
                            <td className="py-1.5 text-foreground">{labelForScreen(row.key)}</td>
                            <td className="py-1.5 text-right tabular-nums">{formatNumber(row.visits)}</td>
                            <td className="py-1.5 text-right tabular-nums">{formatNumber(row.users)}</td>
                            <td className="py-1.5 text-right tabular-nums">{fmtPct(row.users_pct)}</td>
                            <td className="py-1.5 text-right tabular-nums">{formatDurationSec(row.dwell_p50_sec)}</td>
                            <td className="py-1.5 text-right tabular-nums">{formatDurationSec(row.dwell_avg_sec)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

interface ProductEngagementPanelProps {
    days: number;
}

export function ProductEngagementPanel({ days }: ProductEngagementPanelProps) {
    const { data, isLoading, isError, error, refetch, isFetching } = useProductEngagementAnalytics(days);

    if (isLoading && !data) {
        return (
            <div className="rounded-xl border border-border bg-card p-8 flex flex-col items-center gap-2">
                <RefreshCw size={20} className="animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Calculando uso por pestaña y contenido…</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-5 space-y-2">
                <p className="text-sm font-semibold text-rose-400">No se pudo cargar el uso por pestaña</p>
                <p className="text-xs text-muted-foreground">
                    {(error as Error)?.message || 'Error al consultar get_product_engagement_analytics'}
                </p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                    Reintentar
                </button>
            </div>
        );
    }

    if (!data) return null;

    const tabChart = data.main_tabs.map((t) => ({
        name: MAIN_TAB_LABELS[t.key] ?? t.key,
        usuarios: t.users,
        pct: t.users_pct ?? 0,
    }));

    const rinconRows = data.screens.filter((s) => s.group === 'rincon' || s.key.startsWith('rincon'));
    const otherRows = data.screens.filter((s) => s.group === 'other');

    return (
        <section className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Uso por pestaña y contenido</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                        {formatNumber(data.active_users)} usuarios activos en {data.period_days} días
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Recargar uso"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                </button>
            </div>

            <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground mb-3">% de activos que entran a cada pestaña principal</p>
                <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={tabChart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 22%)" />
                            <XAxis dataKey="name" tick={{ fill: 'hsl(215 20% 65%)', fontSize: 11 }} />
                            <YAxis tick={{ fill: 'hsl(215 20% 65%)', fontSize: 11 }} />
                            <Tooltip
                                contentStyle={tooltipStyle}
                                formatter={(value, name) => [
                                    name === 'pct' ? fmtPct(Number(value)) : formatNumber(Number(value)),
                                    name === 'pct' ? '% activos' : 'Usuarios',
                                ]}
                            />
                            <Bar dataKey="usuarios" fill="hsl(199 89% 48%)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {data.main_tabs.map((tab) => (
                    <div key={tab.key} className="rounded-xl border border-border bg-card p-4 min-w-0">
                        <p className="text-xs text-muted-foreground">{MAIN_TAB_LABELS[tab.key] ?? tab.key}</p>
                        <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                            {fmtPct(tab.users_pct)}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                            {formatNumber(tab.users)} usuarios · {formatNumber(tab.visits)} visitas
                        </p>
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
                            <Timer size={12} />
                            p50 {formatDurationSec(tab.dwell_p50_sec)} · media {formatDurationSec(tab.dwell_avg_sec)}
                        </p>
                    </div>
                ))}
            </div>

            <div className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground">Captura, share y cofre</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <LoopCard
                        icon={<Camera size={16} />}
                        title="Crean captura"
                        step={data.capture_loop.creators}
                    />
                    <LoopCard
                        icon={<Share2 size={16} />}
                        title="Comparten en RRSS"
                        step={data.capture_loop.sharers}
                        extra={`${fmtPct(data.capture_loop.sharers.of_creators_pct ?? null)} de quien crea captura`}
                    />
                    <LoopCard
                        icon={<Gift size={16} />}
                        title="Abren el cofre"
                        step={data.capture_loop.chest_openers}
                        extra={`${fmtPct(data.capture_loop.chest_openers.of_creators_pct ?? null)} de quien crea · ${fmtPct(data.capture_loop.chest_openers.of_sharers_pct ?? null)} de quien comparte`}
                    />
                    <LoopCard
                        icon={<Send size={16} />}
                        title="Lanzan objetos"
                        step={data.capture_loop.throwers}
                    />
                </div>
                <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                    <Info size={12} className="mt-0.5 shrink-0" />
                    Cofre: visita a /treasure-chest o apertura confirmada. Lanzamientos: notificaciones treasure_item_sent.
                </p>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <p className="text-sm font-semibold text-foreground">Apartados de Rincón</p>
                <ScreenTable rows={rinconRows} />
            </div>

            {otherRows.length > 0 ? (
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                    <p className="text-sm font-semibold text-foreground">Otras pantallas</p>
                    <ScreenTable rows={otherRows} />
                </div>
            ) : null}

            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <GraduationCap size={16} className="text-primary" />
                    <h4 className="text-sm font-semibold text-foreground">Academia</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="rounded-xl border border-border bg-card p-4 min-w-0">
                        <p className="text-xs text-muted-foreground">Entran a Academia</p>
                        <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                            {formatNumber(data.academy.visitors)}
                            <span className="text-sm font-semibold text-muted-foreground ml-1">
                                ({fmtPct(data.academy.visitors_pct)})
                            </span>
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Sobre usuarios activos</p>
                    </div>
                    <ContentCard title="Leen artículos" stat={data.academy.articles} hint="artículos distintos" />
                    <ContentCard title="Ven vídeos" stat={data.academy.videos} hint="vídeos distintos" />
                </div>
                {data.academy.sections.length > 0 ? (
                    <div className="rounded-xl border border-border bg-card p-4">
                        <p className="text-sm font-semibold text-foreground mb-3">Resto de apartados</p>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] text-muted-foreground text-left">
                                    <th className="pb-2 font-medium">Apartado</th>
                                    <th className="pb-2 font-medium text-right">Visitas</th>
                                    <th className="pb-2 font-medium text-right">Usuarios</th>
                                    <th className="pb-2 font-medium text-right">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.academy.sections.map((row) => (
                                    <tr key={row.key} className="border-t border-border/60">
                                        <td className="py-1.5">{ACADEMY_SECTION_LABELS[row.key] ?? row.key}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.visits)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.users)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{fmtPct(row.users_pct)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                        <Info size={12} className="mt-0.5 shrink-0" />
                        Atlas, Viajes, Embarcación y modalidades se medirán al actualizar la app (evento academy_section_viewed).
                    </p>
                )}
            </div>

            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <ShoppingBag size={16} className="text-primary" />
                    <h4 className="text-sm font-semibold text-foreground">Tienda</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="rounded-xl border border-border bg-card p-4">
                        <p className="text-xs text-muted-foreground">Entran a la tienda</p>
                        <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                            {formatNumber(data.store.visitors)}
                            <span className="text-sm font-semibold text-muted-foreground ml-1">
                                ({fmtPct(data.store.visitors_pct)})
                            </span>
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                            <Users size={12} /> sobre activos
                        </p>
                    </div>
                    <div className="rounded-xl border border-border bg-card p-4">
                        <p className="text-xs text-muted-foreground">Ven fichas de producto</p>
                        <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                            {formatNumber(data.store.product_viewers)}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                            {formatNumber(data.store.product_views)} vistas
                        </p>
                    </div>
                    <div className="rounded-xl border border-border bg-card p-4">
                        <p className="text-xs text-muted-foreground">Checkouts</p>
                        <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                            {formatNumber(data.store.checkouts_started)}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                            {formatNumber(data.store.checkouts_completed)} completados
                        </p>
                    </div>
                    <div className="rounded-xl border border-border bg-card p-4">
                        <p className="text-xs text-muted-foreground">Categorías vistas</p>
                        <p className="text-xl font-bold text-foreground mt-1 tabular-nums">
                            {formatNumber(data.store.sections.length)}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                            Disponible al actualizar la app
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <ProfileTable
                        title="Quién entra, por experiencia"
                        rows={data.store.by_experience_level}
                        labels={EXPERIENCE_LABELS}
                    />
                    <ProfileTable
                        title="Quién entra, por tipo de agua"
                        rows={data.store.by_water_type}
                        labels={WATER_LABELS}
                    />
                </div>

                {data.store.sections.length > 0 ? (
                    <div className="rounded-xl border border-border bg-card p-4">
                        <p className="text-sm font-semibold text-foreground mb-3">Qué categorías ven</p>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] text-muted-foreground text-left">
                                    <th className="pb-2 font-medium">Categoría</th>
                                    <th className="pb-2 font-medium text-right">Visitas</th>
                                    <th className="pb-2 font-medium text-right">Usuarios</th>
                                    <th className="pb-2 font-medium text-right">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.store.sections.map((row) => (
                                    <tr key={row.key} className="border-t border-border/60">
                                        <td className="py-1.5">{STORE_SECTION_LABELS[row.key] ?? row.key}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.visits)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.users)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{fmtPct(row.users_pct)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}

                {data.store.top_products.length > 0 ? (
                    <div className="rounded-xl border border-border bg-card p-4">
                        <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <BookOpen size={14} />
                            Fichas más abiertas (producto / colaborador)
                        </p>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[11px] text-muted-foreground text-left">
                                    <th className="pb-2 font-medium">Producto</th>
                                    <th className="pb-2 font-medium">Tipo</th>
                                    <th className="pb-2 font-medium text-right">Vistas</th>
                                    <th className="pb-2 font-medium text-right">Usuarios</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.store.top_products.map((row) => (
                                    <tr key={`${row.kind}-${row.slug}`} className="border-t border-border/60">
                                        <td className="py-1.5 text-foreground">{row.name}</td>
                                        <td className="py-1.5 text-muted-foreground">
                                            {row.kind === 'gear'
                                                ? 'Equipamiento'
                                                : row.kind === 'merch'
                                                    ? 'Merch'
                                                    : row.kind === 'partner'
                                                        ? 'Colaborador'
                                                        : row.kind === 'pro'
                                                            ? 'Pro'
                                                            : row.kind}
                                        </td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.views)}</td>
                                        <td className="py-1.5 text-right tabular-nums">{formatNumber(row.users)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                        <Info size={12} className="mt-0.5 shrink-0" />
                        Las fichas concretas (producto, colección, categoría) empiezan a contarse con la próxima versión de la app.
                    </p>
                )}
            </div>
        </section>
    );
}
